import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ARTWORKS, WORK_CATEGORIES, COLLECTIONS, getMeta } from "../data/site";
import { ArrowUpRight, ArrowLeft, Mail } from "lucide-react";
import { useCurrency } from "../contexts/CurrencyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NewWorkDrops() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email,
        list: "new-work-drops",
      });
      toast.success("You're on the drops list. New work, first look.");
      setEmail("");
      setSent(true);
    } catch {
      toast.error("Couldn't subscribe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="border-t border-border pt-16 md:pt-24"
      data-testid="new-work-drops"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
        <div className="md:col-span-7">
          <p className="label mb-3">Drops List</p>
          <h2 className="serif text-3xl md:text-5xl tracking-tight font-light leading-[1.05]">
            Get the next drop{" "}
            <span className="italic text-[hsl(var(--ink-soft))]">
              before anyone else.
            </span>
          </h2>
          <p className="mt-4 max-w-xl text-[hsl(var(--ink-soft))] leading-relaxed">
            The Drops List is separate from the general dispatch — one short
            email the moment a new original leaves the studio wall, sent 24
            hours before the piece is posted here.
          </p>
        </div>
        <div className="md:col-span-5">
          {sent ? (
            <div data-testid="drops-sent">
              <p className="label mb-2">On the list</p>
              <p className="serif italic text-xl text-[hsl(var(--ink-soft))]">
                First look, first refusal. Watch your inbox.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="drops-form">
              <div className="flex border-b border-border pb-2">
                <Mail className="h-5 w-5 self-center mr-3 opacity-60" />
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent outline-none placeholder:text-[hsl(var(--ink-soft))]/50 text-base py-3"
                  data-testid="drops-email-input"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center gap-2 label"
                  data-testid="drops-submit"
                >
                  {busy ? "..." : "Notify me"}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-xs text-[hsl(var(--ink-soft))] mt-3">
                One email per new drop. No spam. Never shared.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}


function StatusPill({ status }) {
  if (status === "available")
    return (
      <span className="inline-block bg-[hsl(var(--accent))] text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
        Available
      </span>
    );
  if (status === "sold")
    return (
      <span className="inline-block bg-[hsl(var(--ink-soft))] text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
        Sold
      </span>
    );
  if (status === "on-loan")
    return (
      <span className="inline-block bg-foreground text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
        On Loan · Reserve
      </span>
    );
  return (
    <span className="inline-block bg-foreground text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
      Private
    </span>
  );
}

function PageHeader({ eyebrow, title, blurb, right }) {
  return (
    <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="label mb-4">{eyebrow}</p>
          <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
            {title}
          </h1>
          {blurb && (
            <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
              {blurb}
            </p>
          )}
        </div>
        {right}
      </div>
    </header>
  );
}

function ArtworkCard({ slug, a, testid }) {
  const meta = getMeta(a.id);
  const { format: fmt } = useCurrency();
  return (
    <Link
      to={`/work/${slug}/${a.id}`}
      className="art-card block group"
      data-testid={testid}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
        <img src={a.image} alt={a.title} className="art-img w-full h-full object-cover" />
        <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
          <StatusPill status={meta.status} />
          {meta.prints_coming_soon && (
            <span className="inline-block bg-background text-foreground border border-foreground px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
              Prints Coming Soon
            </span>
          )}
          {meta.prints_available && (
            <span className="inline-block bg-background text-foreground border border-foreground px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
              Prints Available
            </span>
          )}
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="serif italic text-xl md:text-2xl">{a.title}</h3>
          <span className="label">{a.year}</span>
        </div>
        <p className="label mt-1">
          {a.medium} · {a.size}
        </p>
        {(meta.status === "available" || meta.status === "on-loan") && meta.price && (
          <p className="serif text-lg mt-2">{fmt(meta.price)}</p>
        )}
      </div>
    </Link>
  );
}

function FilterBar({ availableOnly, setAvailableOnly }) {
  return (
    <div
      className="flex items-center gap-2 border border-border bg-background p-1"
      data-testid="availability-filter"
    >
      <button
        type="button"
        onClick={() => setAvailableOnly(false)}
        className={`px-4 py-2 text-[0.7rem] uppercase tracking-[0.18em] transition-colors ${
          !availableOnly ? "bg-foreground text-background" : "text-[hsl(var(--ink-soft))]"
        }`}
        data-testid="filter-all"
      >
        All Work
      </button>
      <button
        type="button"
        onClick={() => setAvailableOnly(true)}
        className={`px-4 py-2 text-[0.7rem] uppercase tracking-[0.18em] transition-colors ${
          availableOnly ? "bg-foreground text-background" : "text-[hsl(var(--ink-soft))]"
        }`}
        data-testid="filter-available"
      >
        Available Only
      </button>
    </div>
  );
}

function AdvancedFilters({
  query,
  setQuery,
  priceBand,
  setPriceBand,
  sizeBand,
  setSizeBand,
  sort,
  setSort,
  count,
}) {
  const chip = (active) =>
    `px-3 py-1.5 text-[0.7rem] uppercase tracking-[0.14em] border transition-colors ${
      active
        ? "bg-foreground text-background border-foreground"
        : "bg-background text-[hsl(var(--ink-soft))] border-border hover:text-foreground"
    }`;
  return (
    <div
      className="border-t border-b border-border py-4 md:py-6 grid grid-cols-1 md:grid-cols-[1.4fr_2.2fr_1fr] gap-4 md:gap-8 items-center"
      data-testid="advanced-filters"
    >
      <div className="flex items-center gap-3">
        <span className="label text-[hsl(var(--ink-soft))]">Search</span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="title, subject, medium..."
          className="flex-1 bg-transparent border-b border-border py-1.5 outline-none focus:border-foreground text-sm"
          data-testid="filter-search-input"
        />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="label text-[hsl(var(--ink-soft))] mr-1">Price</span>
        {[
          ["all", "All"],
          ["u3", "Under $3k"],
          ["3to7", "$3k–7k"],
          ["7plus", "$7k+"],
        ].map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setPriceBand(v)}
            className={chip(priceBand === v)}
            data-testid={`filter-price-${v}`}
          >
            {label}
          </button>
        ))}
        <span className="label text-[hsl(var(--ink-soft))] ml-3 mr-1">Size</span>
        {[
          ["all", "All"],
          ["small", "≤ 20\""],
          ["medium", "21–39\""],
          ["large", "40\"+"],
        ].map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setSizeBand(v)}
            className={chip(sizeBand === v)}
            data-testid={`filter-size-${v}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3 md:justify-end">
        <span className="label text-[hsl(var(--ink-soft))]">
          {count} piece{count === 1 ? "" : "s"}
        </span>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-transparent border border-border text-xs uppercase tracking-[0.14em] py-1.5 px-2 outline-none focus:border-foreground"
          data-testid="filter-sort"
        >
          <option value="default">Series order</option>
          <option value="price-asc">Price ↑</option>
          <option value="price-desc">Price ↓</option>
          <option value="year-desc">Newest first</option>
        </select>
      </div>
    </div>
  );
}

export function WorkIndex() {
  const [availableOnly, setAvailableOnly] = useState(false);

  return (
    <>
      <PageHeader
        eyebrow="The Collection"
        title="Work."
        blurb="A growing body of work across figurative painting, Black Western imagery, and abstract contemporary. Browse by series, or contact the studio for studio visits and private viewings."
        right={
          <FilterBar
            availableOnly={availableOnly}
            setAvailableOnly={setAvailableOnly}
          />
        }
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32 space-y-20 md:space-y-28">
        {WORK_CATEGORIES.map((c) => {
          const series = ARTWORKS[c.slug];
          const allItems = series?.items || [];
          const filtered = availableOnly
            ? allItems.filter((a) => ["available", "on-loan"].includes(getMeta(a.id).status))
            : allItems;
          const items = filtered.slice(0, 3);

          if (availableOnly && filtered.length === 0) return null;

          return (
            <section key={c.slug} data-testid={`work-section-${c.slug}`}>
              <div className="flex items-end justify-between gap-6 mb-8 border-b border-border pb-4">
                <div>
                  <p className="label mb-2">
                    Series · {filtered.length} {availableOnly ? "available" : "pieces"}
                  </p>
                  <h2 className="serif text-3xl md:text-5xl tracking-tight font-light">
                    {c.label}
                  </h2>
                </div>
                <Link
                  to={`/work/${c.slug}`}
                  className="inline-flex items-center gap-2 label link-underline"
                  data-testid={`work-explore-${c.slug}`}
                >
                  Explore series <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
                {items.map((a) => (
                  <ArtworkCard
                    key={a.id}
                    slug={c.slug}
                    a={a}
                    testid={`work-item-${a.id}`}
                  />
                ))}
              </div>
            </section>
          );
        })}

        <NewWorkDrops />

        {/* Curator Collections tease */}
        <section
          className="border-t border-border pt-16 md:pt-24"
          data-testid="work-index-collections"
        >
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="label mb-2">Curator Collections</p>
              <h2 className="serif text-3xl md:text-5xl tracking-tight font-light">
                Prefer to browse by feel?
              </h2>
              <p className="mt-4 max-w-xl text-[hsl(var(--ink-soft))] leading-relaxed">
                Hand-picked rooms across every series — by budget, context, and
                studio season.
              </p>
            </div>
            <Link
              to="/collections"
              className="inline-flex items-center gap-2 label link-underline whitespace-nowrap"
              data-testid="work-explore-collections"
            >
              All Collections <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            {COLLECTIONS.map((c) => (
              <Link
                key={c.slug}
                to={`/collections/${c.slug}`}
                className="block border border-border p-6 md:p-8 hover:bg-[hsl(var(--surface))] transition-colors group"
                data-testid={`work-collection-${c.slug}`}
              >
                <p className="label">{c.eyebrow}</p>
                <h3 className="serif text-2xl md:text-3xl mt-3 group-hover:italic transition-all">
                  {c.label}
                </h3>
                <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] leading-relaxed line-clamp-3">
                  {c.blurb}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 label group-hover:text-foreground">
                  Enter <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

export function WorkCategory() {
  const { slug } = useParams();
  const series = ARTWORKS[slug];
  const [availableOnly, setAvailableOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [priceBand, setPriceBand] = useState("all"); // all | u3 | 3to7 | 7plus
  const [sizeBand, setSizeBand] = useState("all"); // all | small | medium | large
  const [sort, setSort] = useState("default"); // default | price-asc | price-desc | year-desc
  const { format: fmt } = useCurrency();

  const items = useMemo(() => {
    let list = (series?.items || []).slice();
    if (availableOnly) {
      list = list.filter((a) => ["available", "on-loan"].includes(getMeta(a.id).status));
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          (a.subject || "").toLowerCase().includes(q) ||
          (a.medium || "").toLowerCase().includes(q)
      );
    }
    if (priceBand !== "all") {
      list = list.filter((a) => {
        const price = getMeta(a.id).price || 0;
        if (price === 0) return false;
        if (priceBand === "u3") return price < 3000;
        if (priceBand === "3to7") return price >= 3000 && price < 7000;
        if (priceBand === "7plus") return price >= 7000;
        return true;
      });
    }
    if (sizeBand !== "all") {
      list = list.filter((a) => {
        const dim = parseInt(String(a.size).replace(/[^0-9].*/, ""), 10) || 0;
        if (sizeBand === "small") return dim > 0 && dim <= 20;
        if (sizeBand === "medium") return dim > 20 && dim < 40;
        if (sizeBand === "large") return dim >= 40;
        return true;
      });
    }
    if (sort === "price-asc") {
      list.sort(
        (a, b) => (getMeta(a.id).price || 0) - (getMeta(b.id).price || 0)
      );
    } else if (sort === "price-desc") {
      list.sort(
        (a, b) => (getMeta(b.id).price || 0) - (getMeta(a.id).price || 0)
      );
    } else if (sort === "year-desc") {
      list.sort((a, b) => (Number(b.year) || 0) - (Number(a.year) || 0));
    }
    return list;
  }, [series, availableOnly, query, priceBand, sizeBand, sort]);

  if (!series) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <Link to="/work" className="label link-underline">
          ← Back to Work
        </Link>
        <h1 className="serif text-5xl mt-6">Series not found.</h1>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24">
        <Link
          to="/work"
          className="inline-flex items-center gap-2 label link-underline"
          data-testid="work-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All Work
        </Link>
      </div>
      <PageHeader
        eyebrow="Series"
        title={series.label}
        blurb={series.blurb}
        right={
          <FilterBar
            availableOnly={availableOnly}
            setAvailableOnly={setAvailableOnly}
          />
        }
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-8 md:mb-12">
        <AdvancedFilters
          query={query}
          setQuery={setQuery}
          priceBand={priceBand}
          setPriceBand={setPriceBand}
          sizeBand={sizeBand}
          setSizeBand={setSizeBand}
          sort={sort}
          setSort={setSort}
          count={items.length}
        />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        {items.length === 0 ? (
          <p className="text-[hsl(var(--ink-soft))]">
            No available pieces in this series at the moment.{" "}
            <Link to="/commissions" className="link-underline text-foreground">
              Commission one
            </Link>
            .
          </p>
        ) : (
          <div
            className={
              slug === "the-posse"
                ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8"
                : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10"
            }
          >
            {items.map((a) => (
              <ArtworkCard
                key={a.id}
                slug={slug}
                a={a}
                testid={`work-item-${a.id}`}
              />
            ))}
          </div>
        )}
        {slug === "the-posse" && (
          <>
            <div
              className="mt-16 md:mt-24 border-t border-[hsl(var(--rule))] pt-10 md:pt-14 bg-foreground text-background -mx-6 md:-mx-10 px-6 md:px-14 py-10 md:py-14"
              data-testid="posse-bundle"
            >
              <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 md:gap-16 items-center">
                <div>
                  <p className="label mb-3 text-background/60">
                    Acquire the whole crew
                  </p>
                  <h3 className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                    All four Posse originals.{" "}
                    <span className="italic text-background/70">
                      Hung as one.
                    </span>
                  </h3>
                  <p className="mt-4 text-background/75 max-w-prose">
                    Krimson, Blue, Yellow, and Purple were painted to hang
                    together — a single cinematic wall. Take the crew home as
                    one acquisition at a bundled price.
                  </p>
                </div>
                <div className="md:text-right">
                  <p className="serif text-4xl md:text-5xl mb-4">
                    {fmt(12000)}
                  </p>
                  <p className="text-xs uppercase tracking-[0.18em] text-background/50 mb-5">
                    Save {fmt(2000)} vs. individual
                  </p>
                  <Link
                    to="/contact?subject=The%20Posse%20-%20full%20crew%20acquisition%20(%2412%2C000)"
                    className="inline-flex items-center gap-3 bg-background text-foreground px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
                    data-testid="posse-bundle-inquire"
                  >
                    Inquire about the crew →
                  </Link>
                </div>
              </div>
            </div>
            <div
              className="mt-14 md:mt-20 border-t border-[hsl(var(--rule))] pt-10 md:pt-14 grid md:grid-cols-[1.2fr_1fr] gap-8 md:gap-16"
              data-testid="posse-companion-print"
            >
              <div>
                <p className="label mb-3">Series companion</p>
                <h3 className="serif text-3xl md:text-4xl leading-[1.1]">
                  A four-panel print of the whole crew.
                </h3>
                <p className="mt-4 text-[hsl(var(--ink-soft))] max-w-prose">
                  Not every wall calls for the originals. A signed, numbered
                  set of The Posse — four archival prints designed to hang as
                  one row — is in production. Reserve your edition by inquiry.
                </p>
              </div>
              <div className="flex md:justify-end items-end">
                <Link
                  to="/contact?subject=The%20Posse%20print%20set"
                  className="inline-flex items-center gap-2 label link-underline"
                  data-testid="posse-print-inquiry-link"
                >
                  Reserve the print set →
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
